/**
 * The build stamp shown in the colophon.
 *
 * `build.ts` injects the source ref and the UTC date separately. They are absent in development,
 * which is the normal case rather than an error.
 */

/**
 * try/catch, NOT a `typeof process` guard — see AGENTS.md §2. Bun inlines the
 * literal only when the variable is set; unset, the bare reference reaches the
 * browser and throws before the app boots, and a `typeof` guard would survive
 * inlining and discard the inlined value.
 */
export function readBuildVersionEnv(): string | undefined {
  try {
    return process.env.BUN_PUBLIC_BUILD_VERSION;
  } catch {
    return undefined;
  }
}

/** Build-time UTC date, never the date the owner happens to open the app. */
export function readBuildDateEnv(): string | undefined {
  try {
    return process.env.BUN_PUBLIC_BUILD_DATE;
  } catch {
    return undefined;
  }
}

export const BUILD_DATE = readBuildDateEnv();

/** A millisecond epoch, used when no Git revision was supplied or available. */
const EPOCH_MS = /^\d{10,}$/;

/**
 * A full commit sha. `pages.yml` passes `github.sha`, so the deployed colophon
 * was printing all forty characters. Requires at least one a-f so a long run of
 * digits that is not a valid date is never mistaken for one.
 */
const LONG_SHA = /^(?=.*[a-f])[0-9a-f]{12,}$/i;

/**
 * `BUILD_VERSION` may be anything the person building chose — a git sha, a tag,
 * a release number — so an unrecognised value is printed as given rather than
 * reformatted into something it is not. The one shape worth translating is
 * a legacy timestamp supplied as the version, because "1756809600000"
 * tells a reader nothing and "2026-09-02" tells them what they wanted to know.
 *
 * ISO rather than a locale format: this string appears in bug reports against
 * the linked repository, and it should not vary by who is reading it.
 */
export function describeBuild(raw: string | undefined): string {
  if (!raw) return "development";
  if (EPOCH_MS.test(raw)) {
    const at = new Date(Number(raw));
    if (!Number.isNaN(at.getTime())) return at.toISOString().slice(0, 10);
  }
  if (LONG_SHA.test(raw.replace(/-dirty$/, "")) && raw.endsWith("-dirty")) return `${raw.slice(0, 7)}-dirty`;
  if (LONG_SHA.test(raw)) return raw.slice(0, 7);
  return raw;
}

export const BUILD_VERSION = describeBuild(readBuildVersionEnv());

/** The build injects its source origin; dev mode uses the public mirror. */
export function readSourceRepositoryEnv(): string | undefined {
  try {
    return process.env.BUN_PUBLIC_SOURCE_REPOSITORY;
  } catch {
    return undefined;
  }
}

export const SOURCE_REPOSITORY = readSourceRepositoryEnv() ?? "https://github.com/StereotypicalCat/budget2.0";

/** A build with an exact revision can link directly to its source. */
export function commitHref(raw: string | undefined, repository = SOURCE_REPOSITORY): string | undefined {
  return raw && /^(?:[0-9a-f]{40}|[0-9a-f]{64})$/i.test(raw)
    ? `${repository}/commit/${raw}`
    : undefined;
}

export const COMMIT_HREF = commitHref(readBuildVersionEnv());
